"use client";

import { type MouseEvent, useEffect, useRef, useState } from "react";
import { Decimal, FEATURE_FLAGS } from "@cede/types";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useArenaMatchmakingTiers } from "@/hooks/automatic-streak/useArenaMatchmakingTiers";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { cn } from "@/lib/utils";
import { CircleHelp } from "lucide-react";

/** Tier enum values are wire constants; spell them for readers. */
const TIER_LABEL: Record<string, string> = {
  PAID: "Paid battles",
  SPIN_AND_GO: "Spin & Go",
};

/** Mirrors Arena's own buy-in labels: whole numbers stay bare, decimals show 2dp. */
const formatBuyIn = (amountUsdt: string): string => {
  const amount = new Decimal(amountUsdt);
  return amount.isInteger() ? amount.toString() : amount.toFixed(Math.max(2, amount.decimalPlaces()));
};

/** Long enough to cross the gap between trigger and panel without it closing. */
const HOVER_CLOSE_DELAY_MS = 120;

/**
 * Capability, not viewport: a touch tablet is wider than the mobile breakpoint
 * but still synthesises mouseenter on tap, which would fight the click toggle.
 */
const useHasHover = () => {
  const [hasHover, setHasHover] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(hover: hover) and (pointer: fine)");
    setHasHover(query.matches);
    const onChange = (event: MediaQueryListEvent) => setHasHover(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return hasHover;
};

/**
 * Explains what a priority ticket does and, when Arena has any configured,
 * lists what each buy-in awards — for playing or for winning, mirroring the
 * backend's `cede_arena:api:priority_tickets_all_participants_enable`.
 *
 * A popover rather than a tooltip so it opens on tap and on keyboard — a
 * hover-only trigger is unreachable on mobile. Hover is layered on top for
 * pointer devices only: a tap also fires mouseenter, which would fight the
 * click toggle.
 */
export const PriorityTicketsInfo = ({ compact = false }: { compact?: boolean }) => {
  const hasHover = useHasHover();
  const [isOpen, setIsOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openOrigin = useRef<"hover" | "interaction" | null>(null);
  const { data, isPending, isError } = useArenaMatchmakingTiers();
  const isAwardedToAllParticipants = useFeatureFlagIsOn(
    FEATURE_FLAGS.CEDE_ARENA_API_PRIORITY_TICKETS_ALL_PARTICIPANTS_ENABLE,
  );

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };

  useEffect(() => cancelClose, []);

  const openOnHover = () => {
    cancelClose();
    openOrigin.current = "hover";
    setIsOpen(true);
  };

  const closeAfterDelay = () => {
    if (openOrigin.current !== "hover") return;
    cancelClose();
    closeTimer.current = setTimeout(() => setIsOpen(false), HOVER_CLOSE_DELAY_MS);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) openOrigin.current = null;
    setIsOpen(open);
  };

  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    const wasHoverOpened = isOpen && openOrigin.current === "hover";
    openOrigin.current = "interaction";
    // Radix toggles on click. A click on a hover-opened trigger adopts the
    // popover instead of immediately closing what the pointer just opened.
    if (wasHoverOpened) event.preventDefault();
  };

  const hoverProps = hasHover ? { onMouseEnter: openOnHover, onMouseLeave: closeAfterDelay } : {};
  const panelHoverProps = hasHover ? { onPointerEnter: cancelClose, onPointerLeave: closeAfterDelay } : {};

  const earningTiers = (data?.tiers ?? [])
    .map((tier) => ({
      tier: tier.tier,
      rows: tier.buyIns.filter((buyIn) => buyIn.autopilotPriorityTickets > 0),
    }))
    .filter((tier) => tier.rows.length > 0);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="How priority tickets work"
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full text-cede-text-secondary transition hover:text-cede-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cede-primary/60",
            compact ? "size-4" : "size-6 border border-cede-border-container hover:border-cede-primary",
          )}
          onClick={handleTriggerClick}
          {...hoverProps}
        >
          <CircleHelp className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        sideOffset={10}
        // Hover must not steal focus from a field; click/keyboard opens keep
        // Radix's focus handoff so assistive technology can reach the panel.
        onOpenAutoFocus={(event) => {
          if (openOrigin.current === "hover") event.preventDefault();
        }}
        // Capped to the viewport so the panel never runs off a phone screen.
        className="w-[min(18rem,calc(100vw-2rem))] space-y-2 rounded-2xl border border-cede-border-container bg-cede-bg-popover p-4 text-sm text-cede-text-secondary shadow-lg"
        {...panelHoverProps}
      >
        <p className="font-semibold text-cede-text-primary">Priority tickets</p>
        <p>Each ticket puts you first in line for one epoch when autopilot slots are picked.</p>

        {isPending ? (
          <p>Loading the earning schedule…</p>
        ) : isError ? (
          <p>The earning schedule is unavailable right now.</p>
        ) : earningTiers.length === 0 ? (
          <p>None are being awarded in Cede Arena at the moment.</p>
        ) : (
          <>
            <p>
              {isAwardedToAllParticipants
                ? "Earn them by playing an Arena battle you paid the buy-in for:"
                : "Earn them by winning an Arena battle you paid the buy-in for:"}
            </p>
            {earningTiers.map((tier) => (
              <div key={tier.tier} className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-cede-text-tertiary">
                  {TIER_LABEL[tier.tier] ?? tier.tier}
                </p>
                <ul className="space-y-0.5">
                  {tier.rows.map((row) => (
                    <li key={row.amountUsdt} className="flex justify-between tabular-nums">
                      <span>${formatBuyIn(row.amountUsdt)}</span>
                      <span className="text-cede-text-primary">
                        {row.autopilotPriorityTickets} ticket{row.autopilotPriorityTickets > 1 ? "s" : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
